import { useState } from 'react';
import type { CustomerSearch } from '@metron/shared';
import { customerSearchSchema } from '@metron/shared';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Label } from '../ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { type FilterChipItem, FilterChipList } from '../search/FilterChipList.tsx';

/** Spec fields a user can add or edit here; paging and sort belong to the table. */
type CustomerFilterField =
  | 'text'
  | 'accountType'
  | 'realEstateAccountType'
  | 'active'
  | 'sensorCountMin'
  | 'sensorCountMax'
  | 'installedCountMin'
  | 'installedCountMax'
  | 'revenueT12mMin'
  | 'revenueT12mMax';

export interface CustomerFilterChip extends FilterChipItem<CustomerFilterField> {
  /** Fields cleared when the chip is removed (min/max pairs share one chip). */
  fields: CustomerFilterField[];
}

function rangeLabel(
  noun: string,
  min: number | undefined,
  max: number | undefined,
  format: (n: number) => string = String,
) {
  if (min !== undefined && max !== undefined) return `${format(min)}–${format(max)} ${noun}`;
  if (min !== undefined) return `≥ ${format(min)} ${noun}`;
  if (max !== undefined) return `≤ ${format(max)} ${noun}`;
  return null;
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** One chip per active filter, in a fixed order. */
export function describeCustomerFilters(spec: CustomerSearch): CustomerFilterChip[] {
  const chips: CustomerFilterChip[] = [];
  if (spec.text !== undefined) {
    chips.push({
      id: 'text',
      label: `Name matches “${spec.text}”`,
      fields: ['text'],
      editable: true,
    });
  }
  if (spec.accountType !== undefined) {
    chips.push({
      id: 'accountType',
      label: spec.accountType,
      fields: ['accountType'],
      editable: true,
    });
  }
  if (spec.realEstateAccountType !== undefined) {
    chips.push({
      id: 'realEstateAccountType',
      label: spec.realEstateAccountType,
      fields: ['realEstateAccountType'],
      editable: true,
    });
  }
  if (spec.active !== undefined) {
    chips.push({
      id: 'active',
      label: spec.active ? 'Active' : 'Inactive',
      fields: ['active'],
      editable: true,
    });
  }
  const assigned = rangeLabel('assigned meters', spec.sensorCountMin, spec.sensorCountMax);
  if (assigned) {
    chips.push({
      id: 'sensorCountMin',
      label: assigned,
      fields: ['sensorCountMin', 'sensorCountMax'],
      editable: true,
    });
  }
  const installed = rangeLabel('installed meters', spec.installedCountMin, spec.installedCountMax);
  if (installed) {
    chips.push({
      id: 'installedCountMin',
      label: installed,
      fields: ['installedCountMin', 'installedCountMax'],
      editable: true,
    });
  }
  // Only Admins can run a revenue search (the API 403s anyone else), so the
  // chip only ever renders for them. Remove-only: the editor has no revenue inputs.
  const revenue = rangeLabel(
    'revenue (T12M)',
    spec.revenueT12mMin,
    spec.revenueT12mMax,
    (n) => usd.format(n),
  );
  if (revenue) {
    chips.push({
      id: 'revenueT12mMin',
      label: revenue,
      fields: ['revenueT12mMin', 'revenueT12mMax'],
      editable: false,
    });
  }
  return chips;
}

/** Drop the given filter fields and return to page 1. */
export function withoutCustomerFields(
  spec: CustomerSearch,
  fields: CustomerFilterField[],
): CustomerSearch {
  const next: CustomerSearch = { ...spec, page: 1 };
  for (const f of fields) delete next[f];
  return next;
}

type CountField = 'sensorCountMin' | 'sensorCountMax' | 'installedCountMin' | 'installedCountMax';

const COUNT_FIELDS: Array<{ field: CountField; label: string }> = [
  { field: 'sensorCountMin', label: 'Assigned meters at least' },
  { field: 'sensorCountMax', label: 'Assigned meters at most' },
  { field: 'installedCountMin', label: 'Installed meters at least' },
  { field: 'installedCountMax', label: 'Installed meters at most' },
];

// Radix Select items can't have an empty value, so "no filter" is a sentinel.
const ANY = 'any';

interface Draft {
  text: string;
  accountType: string;
  realEstateAccountType: string;
  active: string;
  counts: Record<CountField, string>;
}

function toDraft(spec: CustomerSearch): Draft {
  const s = (v: number | undefined) => (v === undefined ? '' : String(v));
  return {
    text: spec.text ?? '',
    accountType: spec.accountType ?? ANY,
    realEstateAccountType: spec.realEstateAccountType ?? ANY,
    active: spec.active === undefined ? ANY : String(spec.active),
    counts: {
      sensorCountMin: s(spec.sensorCountMin),
      sensorCountMax: s(spec.sensorCountMax),
      installedCountMin: s(spec.installedCountMin),
      installedCountMax: s(spec.installedCountMax),
    },
  };
}

interface EditorProps {
  spec: CustomerSearch;
  focusField: CustomerFilterField | null;
  onApply: (next: CustomerSearch) => void;
}

function CustomerFilterEditor({ spec, focusField, onApply }: EditorProps) {
  const [draft, setDraft] = useState<Draft>(() => toDraft(spec));
  const [errors, setErrors] = useState<string[]>([]);

  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    const candidate: Record<string, unknown> = {
      ...spec,
      page: 1,
      text: draft.text.trim() === '' ? undefined : draft.text,
      accountType: draft.accountType === ANY ? undefined : draft.accountType,
      realEstateAccountType: draft.realEstateAccountType === ANY
        ? undefined
        : draft.realEstateAccountType,
      active: draft.active === ANY ? undefined : draft.active === 'true',
    };
    for (const { field } of COUNT_FIELDS) {
      const raw = draft.counts[field].trim();
      candidate[field] = raw === '' ? undefined : Number(raw);
    }
    const parsed = customerSearchSchema.safeParse(
      Object.fromEntries(Object.entries(candidate).filter(([, v]) => v !== undefined)),
    );
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((i) => i.message));
      return;
    }
    setErrors([]);
    onApply(parsed.data);
  };

  const select = (
    id: 'accountType' | 'realEstateAccountType' | 'active',
    label: string,
    options: Array<[string, string]>,
  ) => (
    <div className='space-y-1'>
      <Label htmlFor={`customer-search-${id}`}>{label}</Label>
      <Select
        name={id}
        value={draft[id]}
        onValueChange={(value) => setDraft({ ...draft, [id]: value })}
      >
        <SelectTrigger id={`customer-search-${id}`} autoFocus={focusField === id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any</SelectItem>
          {options.map(([value, text]) => <SelectItem key={value} value={value}>{text}
          </SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <form onSubmit={apply} className='space-y-3' aria-label='Edit filters'>
      <div className='space-y-1'>
        <Label htmlFor='customer-search-text'>Customer name</Label>
        <Input
          id='customer-search-text'
          value={draft.text}
          autoFocus={focusField === 'text'}
          onChange={(e) => setDraft({ ...draft, text: e.target.value })}
        />
      </div>
      <div className='grid grid-cols-3 gap-3'>
        {select('accountType', 'Account type', [['Utility', 'Utility'], [
          'Real Estate',
          'Real Estate',
        ]])}
        {select('realEstateAccountType', 'Real estate type', [['CMFI', 'CMFI'], ['MHP', 'MHP']])}
        {select('active', 'Status', [['true', 'Active'], ['false', 'Inactive']])}
      </div>
      <div className='grid grid-cols-2 gap-3'>
        {COUNT_FIELDS.map(({ field, label }) => (
          <div key={field} className='space-y-1'>
            <Label htmlFor={`customer-search-${field}`}>{label}</Label>
            <Input
              id={`customer-search-${field}`}
              type='number'
              inputMode='numeric'
              min={0}
              step={1}
              value={draft.counts[field]}
              autoFocus={focusField === field}
              onChange={(e) =>
                setDraft({ ...draft, counts: { ...draft.counts, [field]: e.target.value } })}
            />
          </div>
        ))}
      </div>
      {errors.length > 0 && (
        <ul role='alert' className='text-sm text-destructive list-disc pl-5'>
          {/* Replaced wholesale on each submit, and two issues can share a message. */}
          {errors.map((m, i) => <li key={i}>{m}</li>)}
        </ul>
      )}
      <div className='flex justify-end'>
        <Button type='submit' size='sm'>Apply</Button>
      </div>
    </form>
  );
}

interface CustomerSearchFiltersProps {
  spec: CustomerSearch;
  onChange: (next: CustomerSearch) => void;
}

/** Active customer filters as chips plus an Add filter editor. */
export function CustomerSearchFilters({ spec, onChange }: CustomerSearchFiltersProps) {
  return (
    <FilterChipList
      chips={describeCustomerFilters(spec)}
      emptyText='No filters: showing all customers.'
      onRemove={(chip) => onChange(withoutCustomerFields(spec, chip.fields))}
      renderEditor={(focusField, close) => (
        <CustomerFilterEditor
          spec={spec}
          focusField={focusField}
          onApply={(next) => {
            close();
            onChange(next);
          }}
        />
      )}
    />
  );
}
